import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { AuthDownloadService } from '../../../../../../../services/auth-download.service';
import { createArchiveDownloadCallbacks } from '../../../../../../../services/archive-download-feedback.util';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, finalize, of, take } from 'rxjs';
import { CaseManagementService } from '../../../../../../../../services/case-management.service';
import { CaseArchiveModel } from '@models/case-archive.model';
import { ArchiveStatus } from '@models/archive.model';
import { CaseAnnotateDialogComponent } from '../../../../../../../../pages/case-management/dialogs/case-annotate-dialog/case-annotate-dialog.component';
import { VideoSignatureModalComponent } from '../../../../../../video-signature-modal/video-signature-modal.component';
import { AuthSrcDirective } from '../../../../../../../directives/auth-src.directive';
import {
  ConfirmDialogModel,
  ConfirmDialogService,
  PreloaderColor,
  UiPreloaderComponent,
} from '@ui';

@Component({
  selector: 'app-case-manager-clips-preview',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TranslatePipe,
    AuthSrcDirective,
    UiPreloaderComponent,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
  templateUrl: './case-manager-clips-preview.component.html',
  styleUrls: ['../case-manager-feature-preview.shared.scss'],
})
export class CaseManagerClipsPreviewComponent implements OnInit {
  private readonly caseManagementService = inject(CaseManagementService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly confirm = inject(ConfirmDialogService);
  private readonly authDownloadService = inject(AuthDownloadService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  readonly caseId = input.required<string>();
  readonly canDownload = input<boolean>(false);
  readonly canEdit = input<boolean>(false);

  protected readonly preloaderColor = PreloaderColor.blue;
  protected readonly fallbackThumbnail = 'assets/thumb_placeholder.png';
  protected readonly archives = signal<
    CaseArchiveModel.ArchiveWithCommentsAndAnnotations[]
  >([]);
  protected readonly isLoading = signal(false);
  protected readonly downloadingSessionId = signal<string | null>(null);
  protected readonly deletingId = signal<string | null>(null);

  public ngOnInit(): void {
    this.loadArchives();
  }

  protected openArchive(
    archive: CaseArchiveModel.ArchiveWithCommentsAndAnnotations,
  ): void {
    this.dialog.open(CaseAnnotateDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '110vh',
      height: '80vh',
      disableClose: true,
      data: {
        archive,
        canEdit: false,
      },
    });
  }

  protected canDownloadArchive(
    archive: CaseArchiveModel.ArchiveWithCommentsAndAnnotations,
  ): boolean {
    return (
      this.canDownload() &&
      !!archive.sessionId &&
      archive.files?.videos?.length === 1
    );
  }

  protected downloadArchive(
    archive: CaseArchiveModel.ArchiveWithCommentsAndAnnotations,
  ): void {
    if (!this.canDownloadArchive(archive) || !archive.sessionId) {
      return;
    }

    this.downloadingSessionId.set(archive.sessionId);
    this.caseManagementService
      .getCaseArchiveDownloadUrl(this.caseId(), archive.sessionId)
      .pipe(
        finalize(() => this.downloadingSessionId.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ url, filename, mediaStorageSource }) => {
        const resolvedUrl =
          this.caseManagementService.resolveCaseClipDownloadUrl(
            url,
            archive,
            mediaStorageSource,
          );
        this.authDownloadService.download(
          resolvedUrl,
          filename,
          createArchiveDownloadCallbacks(
            this.toastrService,
            this.translateService,
          ),
        );
      });
  }

  protected isVideoSignatureDisabled(
    archive: CaseArchiveModel.ArchiveWithCommentsAndAnnotations,
  ): boolean {
    return (
      archive.status !== ArchiveStatus.COMPLETED_TRANSCODE || !archive.files
    );
  }

  protected videoSignature(
    archive: CaseArchiveModel.ArchiveWithCommentsAndAnnotations,
  ): void {
    if (this.isVideoSignatureDisabled(archive)) {
      return;
    }

    this.dialog.open(VideoSignatureModalComponent, {
      panelClass: 'modal-no-padding',
      data: {
        signature: archive.md5Sum,
        videos: archive.files?.videos,
      },
    });
  }

  protected deleteClip(
    archive: CaseArchiveModel.ArchiveWithCommentsAndAnnotations,
  ): void {
    if (!this.canEdit() || this.deletingId() || !archive._id) {
      return;
    }
    const clipId = archive._id;

    this.confirm
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'COMMON.DELETE',
        msg: 'CONFIRM_MODALS.COMMON_MSG',
        params: { name: archive.name },
        confirm: 'CONFIRM_MODALS.COMMON_YES',
        cancel: 'COMMON.CANCEL',
        disableClose: true,
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.selection !== ConfirmDialogModel.ConfirmResult.OK) {
          return;
        }
        this.deletingId.set(clipId);
        this.caseManagementService
          .removeClip(this.caseId(), clipId)
          .pipe(
            finalize(() => this.deletingId.set(null)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe(() =>
            this.archives.update((list) =>
              list.filter((item) => item._id !== clipId),
            ),
          );
      });
  }

  protected isBusy(
    archive: CaseArchiveModel.ArchiveWithCommentsAndAnnotations,
  ): boolean {
    return (
      this.downloadingSessionId() === archive.sessionId ||
      this.deletingId() === archive._id
    );
  }

  private loadArchives(): void {
    this.isLoading.set(true);
    this.caseManagementService
      .getCaseArchives(this.caseId())
      .pipe(
        catchError(() => of([])),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((archives) => this.archives.set(archives));
  }
}
